import * as THREE from 'three';
import { parseSeed } from '../gen/rng';
import { threeToGen } from './coords';
import { DebugOverlay, blockLayer, buildableLayer, footprintLayer, interiorLayer, rejectedLayer, roadLayer } from './debugOverlay';
import { InteriorManager } from './interiorManager';
import { SolidOverlay } from './solidOverlay';
import { SkyRig } from './sky';
import { Shadows } from './shadows';
import { PostFx } from './postfx';
import type { GraphicsSettings, Quality } from './hud';
import { FlyCamera } from './flyCamera';
import { Hud } from './hud';
import { Navigation } from './navigation';
import { CROWD, TrafficSystem } from './traffic/trafficSystem';
import { ModelLibrary } from './models/library';
import { carPaintMaterial } from './models/materials';
import { SurfaceTextures } from './surfaceTextures';
import { Benchmark } from './bench';
import { AudioEngine } from './audio/audioEngine';
import { GameHud } from './ui/gameHud';
import { Menu, type PlayMode } from './ui/menu';
import { CityAudio, VOICES } from './audio/cityAudio';
import type { ListenerEnv } from './audio/mixLogic';
import { GpuTimer } from './gpuTimer';
import { TileManager, ignoreCancel } from './tileManager';
import { WorkerPool } from './workerPool';

/** Tone-mapping exposure outdoors (set by the sky's brightness) and inside rooms, and the adaptation time. */
const EXPOSURE = { outdoor: 0.25, indoor: 1.1, seconds: 0.6 };

export class App {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly fly: FlyCamera;
  readonly pool: WorkerPool;
  readonly hud: Hud;
  readonly tiles: TileManager;
  readonly overlay: DebugOverlay;
  readonly interiors: InteriorManager;
  readonly solid: SolidOverlay;
  readonly skyRig: SkyRig;
  readonly shadows: Shadows;
  readonly postfx: PostFx;
  readonly nav: Navigation;
  readonly traffic: TrafficSystem;
  readonly models: ModelLibrary;
  readonly surfaces: SurfaceTextures;
  readonly gpu: GpuTimer;
  readonly audio: AudioEngine;
  readonly sound: CityAudio;
  /** Terrain height (m) under the camera, probed twice a second for the audio; trees near it (0 … 1). */
  private groundY = 0;
  private treesNear = 0;
  private listenT = 0;
  private readonly lastCam = new THREE.Vector3();
  private camSpeed = 0;
  readonly menu: Menu;
  readonly gameHud: GameHud;
  private readonly crosshair: HTMLDivElement;
  /** A way in chosen on the menu, waiting for the player to be placed. */
  private choosing: PlayMode | null = null;
  private wasLocked = false;
  private orbit = 0;
  private hudHidden = false;
  /** Dynamic resolution: the preset's pixel ratio, the scale on it, and recent frame costs (ms). */
  private basePixelRatio = 1;
  resScale = 1;
  /** The preset's MSAA samples (dynamic resolution drops to 2 before lowering the scale). */
  private msaaWanted = 4;
  private readonly dynres: boolean;
  private readonly costs: number[] = [];
  private resT = 0;
  private attractY: number | null = null;
  private boxes: { x0: number; y0: number; x1: number; y1: number; top: number }[] = [];
  private boxesVersion = -1;
  private readonly started = performance.now();
  private readonly bench: Benchmark | null = null;
  graphics: GraphicsSettings;
  seed: number;
  private seedText: string;
  private last = performance.now();
  private frameMsAvg = 16;
  private lastOverlay = 0;

  constructor(private readonly container: HTMLElement) {
    const params = new URLSearchParams(location.search);
    this.dynres = params.get('dynres') !== '0';
    this.seedText = params.get('seed') ?? '1337';
    this.seed = parseSeed(this.seedText);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.domElement.className = 'main';
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = EXPOSURE.outdoor;
    container.appendChild(this.renderer.domElement);

    this.skyRig = new SkyRig(this.renderer, this.scene);
    this.camera = new THREE.PerspectiveCamera(65, container.clientWidth / container.clientHeight, 0.3, 8000);
    this.camera.position.set(0, 120, 0);

    // Faint sky/ground bounce on top of the image-based light from the sky's environment map.
    this.scene.add(new THREE.HemisphereLight(0xdfefff, 0x6b604c, 0.25));

    this.fly = new FlyCamera(this.camera, this.renderer.domElement);
    const workers = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 4) - 1));
    this.pool = new WorkerPool(workers);

    const radius = Number(params.get('radius') ?? 8);
    const cityRadius = Number(params.get('city') ?? 2);
    const nearRadius = Number(params.get('near') ?? 1);
    this.tiles = new TileManager(this.pool, this.seed, { viewRadius: radius, applyBudgetMs: 4, cityRadius, nearRadius, imposterRadius: Number(params.get('far') ?? 4) });
    this.scene.add(this.tiles.root);
    this.shadows = new Shadows(this.renderer, this.scene, this.camera, this.skyRig.sun, this.skyRig.sunDir);
    this.surfaces = new SurfaceTextures(params.get('quality') === 'low' ? 256 : 512);
    this.tiles.applySurfaces(this.surfaces);
    void this.surfaces.load().catch((e) => console.error('surface textures', e));
    this.tiles.registerMaterials(this.shadows);
    this.postfx = new PostFx(this.renderer, this.scene, this.camera);
    // Settings: the URL wins, then what the player chose last time, then the mid preset.
    let saved: Partial<GraphicsSettings> = {};
    try {
      saved = JSON.parse(localStorage.getItem(GRAPHICS_STORE) ?? '{}') as Partial<GraphicsSettings>;
    } catch {
      /* storage unavailable */
    }
    const pick = (v: string | null | undefined) => (v === 'low' || v === 'mid' || v === 'high' ? v : null);
    const quality: Quality = pick(params.get('quality')) ?? pick(saved.quality) ?? 'mid';
    const flag = (k: 'shadows' | 'ao') => (params.has(k) ? params.get(k) !== '0' : typeof saved[k] === 'boolean' && pick(saved.quality) === quality ? saved[k]! : quality !== 'low');
    this.graphics = { quality, shadows: flag('shadows'), ao: flag('ao') };
    this.applyGraphics(this.graphics);
    this.renderer.info.autoReset = false;
    this.interiors = new InteriorManager(this.pool, this.tiles, this.seed, this.surfaces, { nearDistance: 6000, farDistance: 9000, floorSpan: 1, maxBuildings: 3, maxHouses: 6 });
    this.scene.add(this.interiors.root);
    this.interiors.prepare = (g) => this.renderer.compileAsync(g, this.camera, this.scene);
    this.solid = new SolidOverlay(container, this.tiles, this.interiors, () => this.seed);

    this.crosshair = document.createElement('div');
    this.crosshair.className = 'crosshair';
    container.appendChild(this.crosshair);

    this.overlay = new DebugOverlay(container);
    const tilesIt = () => this.tiles.tiles.values();
    this.overlay.addLayer(buildableLayer(tilesIt));
    this.overlay.addLayer(blockLayer(tilesIt));
    this.overlay.addLayer(footprintLayer(tilesIt));
    this.overlay.addLayer(roadLayer(tilesIt));
    this.overlay.addLayer(rejectedLayer(tilesIt));
    this.overlay.addLayer(interiorLayer(this.interiors));
    this.overlay.onPick = (x, y) => {
      const g = threeToGen(this.camera.position);
      this.interiors.pick(x, y, g.z);
    };

    // Sound starts on the first click or key press (browsers require a gesture); ?audio=0 turns it off.
    this.audio = new AudioEngine(params.get('audio') !== '0');
    this.sound = new CityAudio(this.audio);
    this.sound.voiceCount = VOICES[this.graphics.quality];
    this.hud = new Hud(container, this.seedText, {
      onRegenerate: (text) => this.regenerate(text),
      onToggleOverlay: (on) => this.overlay.setVisible(on),
      onGraphics: (gs) => this.applyGraphics(gs),
      onAudio: (volume, muted) => this.audio.setVolume(volume, muted),
      audioState: () => ({ volume: this.audio.volume, muted: this.audio.muted, running: this.audio.ctx?.state === 'running' }),
    }, this.graphics, params.get('debug') === '1');
    if (params.get('overlay') === '1') this.overlay.setVisible(true);

    // CC0 models for cars, trees and people (public/models); the built-in shapes stand in until loaded.
    this.models = new ModelLibrary();
    this.models.onLoad(() => this.tiles.assets.useModels(this.models, (m) => this.shadows.setup(m)));
    void this.models.load().catch((e) => console.error('models', e));
    this.traffic = new TrafficSystem(this.tiles, (m) => this.shadows.setup(m), this.models);
    this.traffic.counts = CROWD[this.graphics.quality];
    this.scene.add(this.traffic.root);
    this.nav = new Navigation(this.camera, this.fly, this.scene, this.tiles, this.interiors, this.hud, this.shadows);
    this.nav.trafficPoses = () => this.traffic.sim.cars.map((c) => c.cur);
    this.nav.carModel = () => {
      const car = this.models.cars.find((c) => c.name === 'car-sports');
      if (!car) return null;
      // Red paint on the body panels only (a plain mesh has no instance colour).
      const m = carPaintMaterial();
      m.onBeforeCompile = (sh) => {
        sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nattribute float carPaint;').replace('#include <color_vertex>', '#include <color_vertex>\nvColor.xyz = mix(color.xyz, vec3(0.55, 0.05, 0.04), carPaint);');
      };
      m.customProgramCacheKey = () => 'playerCar';
      this.shadows.setup(m);
      return { geometry: car.geometry, material: m };
    };
    this.gpu = new GpuTimer(this.renderer.getContext() as WebGL2RenderingContext);
    if (params.get('bench') === '1') this.bench = new Benchmark(this.camera, this.fly, this.tiles, this.pool, this.gpu, container);
    this.gameHud = new GameHud(container);
    this.menu = new Menu(container, {
      onPlay: (m) => this.play(m),
      onResume: () => this.resume(),
      onGraphics: (gs) => this.applyGraphics(gs),
      onAudio: (volume, muted) => this.audio.setVolume(volume, muted),
      onRegenerate: (text) => this.regenerate(text),
      onMouse: (sensitivity, invertY) => this.fly.setMouse(sensitivity, invertY),
      state: () => ({ seed: this.seedText, graphics: this.graphics, volume: this.audio.volume, muted: this.audio.muted, sensitivity: this.fly.sensitivity, invertY: this.fly.invertY }),
    });
    this.nav.onDoor = () => this.sound.door();
    // Menu buttons click (the first click also starts the audio, so it may be silent).
    this.menu.el.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button')) this.audio.click();
    });
    // H hides the HUD (for screenshots).
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (e.code !== 'KeyH' || t?.tagName === 'INPUT') return;
      this.hudHidden = !this.hudHidden;
      this.syncChrome();
    });
    this.nav.onMode = (m) => {
      this.gameHud.showHint(HINTS[m]);
      this.syncChrome();
    };
    const mode = params.get('mode');
    // A mode in the URL (or the benchmark, or ?title=0) skips the title screen.
    if (mode === 'walk' || mode === 'drive') this.nav.setMode(mode);
    if (mode || this.bench || params.get('title') === '0') this.menu.hide();
    else this.menu.showTitle();
    this.syncChrome();
    const canvas = this.renderer.domElement;
    document.addEventListener('pointerlockchange', () => {
      const locked = document.pointerLockElement === canvas;
      if (locked) {
        this.wasLocked = true;
        if (this.menu.mode === 'pause') this.menu.hide();
      } else if (this.wasLocked && this.menu.mode === 'hidden' && !this.overlay.shown && !this.solid.active) {
        this.menu.showPause();
      }
      if (!locked) this.wasLocked = false;
      this.syncChrome();
    });
    // With a menu up, 1/2/3 are its buttons (captured before navigation sees them).
    window.addEventListener(
      'keydown',
      (e) => {
        const m = ({ Digit1: 'fly', Digit2: 'walk', Digit3: 'drive' } as const)[e.code as 'Digit1'];
        const t = e.target as HTMLElement | null;
        if (!m || this.menu.mode === 'hidden' || t?.tagName === 'INPUT') return;
        e.stopPropagation();
        this.play(m);
      },
      { capture: true },
    );
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Escape' || this.menu.mode === 'title') return;
      // Without pointer lock (it was refused, or released on the map), Esc toggles the menu.
      if (this.menu.mode === 'pause') this.resume();
      else if (document.pointerLockElement !== canvas) this.menu.showPause();
      this.syncChrome();
    });

    window.addEventListener('resize', () => this.onResize());
    this.placeCameraAboveGround();
  }

  /**
   * Dynamic resolution, to hold 30 fps: every 2 s, the median cost of the last frames (GPU time when
   * the timer query is available, else frame time) steps down when over 28 ms (first 4× MSAA to 2×,
   * then the render scale by 0.1 to 0.6) and back up when under 17 ms. The median ignores loading hitches; frame time from the
   * CPU side can't be helped by fewer pixels, which is why GPU time is preferred. Off with ?dynres=0
   * and during the benchmark.
   */
  private adaptResolution(dt: number, frameMs: number): void {
    if (!this.dynres || this.bench || this.solid.active || document.hidden) return;
    this.costs.push(this.gpu.last ?? frameMs);
    if (this.costs.length > 240) this.costs.shift();
    this.resT += dt;
    if (this.resT < 2 || this.costs.length < 30) return;
    this.resT = 0;
    const sorted = [...this.costs].sort((a, b) => a - b);
    const median = sorted[sorted.length >> 1];
    // Ladder: full scale with the preset's MSAA → 2× MSAA → scale down in 0.1 steps (and back up).
    const pf = this.postfx;
    let next = this.resScale;
    let msaa = pf.msaa;
    if (median > 28) {
      if (msaa > 2) msaa = 2;
      else next = Math.max(0.6, this.resScale - 0.1);
    } else if (median < 17) {
      if (this.resScale < 1) next = Math.min(1, this.resScale + 0.1);
      else if (msaa < this.msaaWanted) msaa = this.msaaWanted;
    }
    if (Math.abs(next - this.resScale) < 0.01 && msaa === pf.msaa) return;
    this.resScale = Math.round(next * 10) / 10;
    pf.msaa = msaa;
    this.costs.length = 0;
    this.renderer.setPixelRatio(this.basePixelRatio * this.resScale);
    this.onResize();
  }

  /** Show the HUD, crosshair and menus that fit the moment; duck the sound while paused. */
  private syncChrome(): void {
    const inMenu = this.menu.mode !== 'hidden';
    this.gameHud.visible = !this.hudHidden && (!inMenu || this.menu.mode === 'pause');
    this.gameHud.el.classList.toggle('dim', this.menu.mode === 'pause');
    this.crosshair.style.display = !inMenu && !this.hudHidden && this.nav.mode !== 'drive' ? '' : 'none';
    this.audio.duck(this.menu.mode === 'pause' ? 0.35 : 1);
  }

  /** A way in from the title or pause menu: fly from here, or find a street (walk) or road (drive). */
  private play(mode: PlayMode): void {
    this.audio.unlock();
    const canvas = this.renderer.domElement;
    const lock = () => {
      try {
        void (canvas.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => {});
      } catch {
        /* refused (headless, or too soon after Esc): the menu stays usable */
      }
    };
    if (mode === 'fly') {
      this.nav.setMode('fly');
      this.fly.pitch = Math.max(-0.5, this.fly.pitch);
      this.choosing = null;
      this.menu.hide();
      this.syncChrome();
      lock();
      return;
    }
    if (this.nav.mode !== mode || !this.nav.player) {
      const spot = this.streetSpot(mode === 'walk' ? 'sidewalk' : 'road');
      if (spot) {
        this.camera.position.set(spot.x, Math.max(this.camera.position.y, this.groundY + 40), spot.z);
      }
      // Level the view (the title camera looks down on the city).
      this.fly.pitch = mode === 'walk' ? -0.05 : -0.3;
      this.nav.setMode(mode);
      this.choosing = mode;
      this.menu.setBusy(mode === 'walk' ? 'Finding a street…' : 'Finding a car…');
    } else {
      this.menu.hide();
      this.syncChrome();
    }
    lock();
  }

  private resume(): void {
    this.menu.hide();
    this.syncChrome();
    try {
      void (this.renderer.domElement.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => {});
    } catch {
      /* refused: play on without it */
    }
  }

  /**
   * A place to start near the camera: the middle of a sidewalk at a block corner (walk), or a road
   * junction (drive). Render-space x, z.
   */
  private streetSpot(kind: 'sidewalk' | 'road'): { x: number; z: number } | null {
    const g = threeToGen(this.camera.position);
    let best: { x: number; y: number; d: number } | null = null;
    const consider = (x: number, y: number) => {
      const d = (x - g.x) ** 2 + (y - g.y) ** 2;
      if (!best || d < best.d) best = { x, y, d };
    };
    for (const t of this.tiles.tiles.values()) {
      if (t.ring > 1) continue;
      if (kind === 'road') {
        const n = t.roads?.graph.nodes ?? [];
        for (let i = 0; i < n.length; i += 2) consider(n[i], n[i + 1]);
        continue;
      }
      for (const b of t.blocks ?? []) {
        // A sidewalk corner, 1.5 m in from the edge (where the pedestrians walk).
        const p = b.sidewalk;
        if (p.length < 6) continue;
        let cx = 0;
        let cy = 0;
        for (let i = 0; i < p.length; i += 2) {
          cx += p[i];
          cy += p[i + 1];
        }
        cx /= p.length / 2;
        cy /= p.length / 2;
        for (let i = 0; i < p.length; i += 2) {
          const d = Math.hypot(cx - p[i], cy - p[i + 1]) || 1;
          consider(p[i] + ((cx - p[i]) / d) * 150, p[i + 1] + ((cy - p[i + 1]) / d) * 150);
        }
      }
    }
    const b = best as { x: number; y: number } | null;
    return b ? { x: b.x / 100, z: -b.y / 100 } : null;
  }

  /** Loading progress for the title: near tiles' terrain and city, the textures and the models. */
  private loadProgress(): { f: number; ready: boolean } {
    let need = 0;
    let have = 0;
    for (const t of this.tiles.tiles.values()) {
      if (t.ring > 1) continue;
      need += 2;
      if (t.terrain && t.shownLod === t.goalLod) have++;
      if (t.city) have++;
    }
    const f = 0.65 * (need ? have / need : 0) + 0.2 * (this.surfaces.loaded ? 1 : 0) + 0.15 * (this.models.loaded ? 1 : 0);
    return { f, ready: f > 0.999 || performance.now() - this.started > 90000 };
  }

  /**
   * The title's slow orbit over the city, lifted clear of any building near the path ahead (towers
   * would otherwise put the camera inside an office).
   */
  private attract(dt: number): void {
    this.orbit += dt * 0.035;
    const r = 280;
    const at = (a: number) => ({ x: Math.cos(a) * r, z: Math.sin(a) * r });
    // Building boxes (generation cm, grown by 30 m) and tops (m), rebuilt when the tiles change.
    if (this.boxesVersion !== this.tiles.dataVersion) {
      this.boxesVersion = this.tiles.dataVersion;
      this.boxes = [];
      for (const t of this.tiles.tiles.values()) {
        if (t.ring > 1) continue;
        for (const bld of t.buildings ?? []) {
          if (bld.kind === 'park') continue;
          const f = bld.footprint;
          const b = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity, top: (bld.baseZ + bld.floors * bld.floorHeight) / 100 };
          for (let i = 0; i < f.length; i += 2) {
            b.x0 = Math.min(b.x0, f[i] - 3000);
            b.x1 = Math.max(b.x1, f[i] + 3000);
            b.y0 = Math.min(b.y0, f[i + 1] - 3000);
            b.y1 = Math.max(b.y1, f[i + 1] + 3000);
          }
          this.boxes.push(b);
        }
      }
    }
    // Tallest building near the next few seconds of the path.
    let top = -Infinity;
    for (let k = 0; k <= 6; k++) {
      const p = at(this.orbit + k * 0.06);
      const gx = p.x * 100;
      const gy = -p.z * 100;
      for (const b of this.boxes) if (gx > b.x0 && gx < b.x1 && gy > b.y0 && gy < b.y1) top = Math.max(top, b.top);
    }
    const want = Math.max(this.groundY + 110, top + 75);
    this.attractY = this.attractY === null ? want : this.attractY + (want - this.attractY) * Math.min(1, dt * 0.8);
    const p = at(this.orbit);
    this.camera.position.set(p.x, this.attractY, p.z);
    this.fly.lookAt(new THREE.Vector3(0, this.groundY + 12, 0));
  }

  /** Put the camera a little above the terrain at its current XY (asks a worker for the height). */
  placeCameraAboveGround(): void {
    const g = threeToGen(this.camera.position);
    void this.pool
      .submit('probe', this.seed, { points: [g.x, g.y] }, -1)
      .then((r) => {
        this.camera.position.y = r.heights[0] / 100 + 60;
      })
      .catch(ignoreCancel);
  }

  regenerate(seedText: string): void {
    this.seedText = seedText;
    this.seed = parseSeed(seedText);
    const url = new URL(location.href);
    url.searchParams.set('seed', seedText);
    history.replaceState(null, '', url);
    this.hud.setSeed(seedText);
    this.menu.refresh();
    this.tiles.setSeed(this.seed);
    this.interiors.setSeed(this.seed);
    this.nav.setMode('fly');
    this.placeCameraAboveGround();
  }

  start(): void {
    const tick = () => {
      requestAnimationFrame(tick);
      this.renderer.info.reset();
      const now = performance.now();
      const rawMs = now - this.last;
      const dt = Math.min(0.1, rawMs / 1000);
      this.frameMsAvg = this.frameMsAvg * 0.9 + (now - this.last) * 0.1;
      this.last = now;
      this.bench?.update(now);
      const paused = this.menu.mode === 'pause';
      if (this.menu.mode === 'title' && !this.choosing) {
        this.attract(dt);
        const p = this.loadProgress();
        this.menu.setProgress(p.f, p.ready ? 'Ready' : `Building the city… ${Math.round(p.f * 100)} %`, p.ready);
      }
      this.fly.enabled = this.nav.mode === 'fly' && this.menu.mode === 'hidden';
      this.fly.update(dt);
      if (!paused) this.nav.update(dt);
      if (this.choosing && (this.nav.player || this.nav.mode !== this.choosing)) {
        // Placed (or it couldn't start: the status line says why).
        this.choosing = null;
        this.menu.hide();
        this.syncChrome();
      }
      if (!paused) this.traffic.update(dt, this.camera, this.nav.obstacles);
      const g = threeToGen(this.camera.position);
      this.tiles.update(g.x, g.y);
      this.interiors.update(g.x, g.y, g.z, now);
      this.camera.updateMatrixWorld();
      this.listen(dt, g.x, g.y, g.z);
      // Eye adaptation: interiors are far dimmer than daylight; ease exposure in log space.
      const targetExposure = this.solid.indoors(g.x, g.y, g.z) ? EXPOSURE.indoor : EXPOSURE.outdoor;
      const k = 1 - Math.exp(-dt / EXPOSURE.seconds);
      this.renderer.toneMappingExposure = Math.exp(Math.log(this.renderer.toneMappingExposure) + (Math.log(targetExposure) - Math.log(this.renderer.toneMappingExposure)) * k);
      this.skyRig.update(this.camera);
      this.shadows.update();
      this.solid.update(g.x, g.y, g.z, this.camera);
      // The material view covers the screen: skip the 3D render while it's up.
      if (!this.solid.active) {
        this.gpu.begin();
        this.postfx.render();
        this.gpu.end();
      }
      this.bench?.record(rawMs, this.renderer.info.render);
      this.adaptResolution(dt, rawMs);
      this.overlay.cameraX = g.x;
      this.overlay.cameraY = g.y;
      this.overlay.cameraYaw = this.nav.heading;
      if (now - this.lastOverlay > 120) {
        this.lastOverlay = now;
        this.overlay.draw(this.tiles.tiles.values(), this.tiles.dataVersion);
      }
      const carV = this.nav.carSpeed;
      this.gameHud.update(now, {
        mode: this.nav.mode,
        car: carV !== null ? { kmh: carV * 3.6, gear: this.sound.gearbox.gear, rev: this.sound.gearbox.rev } : null,
        tiles: this.tiles.tiles.values(),
        gx: g.x,
        gy: g.y,
        yaw: this.nav.heading,
        height: this.camera.position.y - this.groundY,
        status: this.nav.statusText || this.nav.prompt,
      });
      this.hud.update({
        frameMs: this.frameMsAvg,
        gpuMs: this.gpu.ms,
        render: this.renderer.info.render,
        fps: 1000 / this.frameMsAvg,
        activeTiles: this.tiles.tiles.size,
        queueDepth: this.pool.queueDepth,
        inFlight: this.pool.inFlight,
        applyQueue: this.tiles.applyQueueLength,
        extra: `render scale ${this.resScale.toFixed(1)} · msaa ${this.postfx.msaa}× · workers ${this.pool.size} · last job ${this.pool.lastJobMs.toFixed(0)} ms<br/>interior floors ${this.interiors.floors.size} (+${this.interiors.pendingCount} pending)${this.selectedText()}<br/>traffic ${this.traffic.sim.cars.length} cars · ${this.traffic.peds.peds.length} people · ${this.traffic.net.lanes.size} lanes<br/>${this.nav.hudText()}${this.nav.mode === 'fly' ? ` · speed ${this.fly.speed.toFixed(0)} m/s` : ''} · pos ${(g.x / 100).toFixed(0)}, ${(g.y / 100).toFixed(0)}, ${(g.z / 100).toFixed(0)} m`,
      });
    };
    tick();
  }

  /** Update the city's sound for where the camera is (ambience inputs refreshed twice a second). */
  private listen(dt: number, gx: number, gy: number, gz: number): void {
    const cam = this.camera.position;
    // Jumps (a mode switch, the chase camera settling) aren't movement.
    const moved = cam.distanceTo(this.lastCam);
    if (moved < 6) this.camSpeed += (moved / Math.max(dt, 1e-3) - this.camSpeed) * Math.min(1, dt * 5);
    this.lastCam.copy(cam);
    this.listenT -= dt;
    if (this.listenT <= 0) {
      this.listenT = 0.5;
      void this.pool
        .submit('probe', this.seed, { points: [gx, gy] }, -1)
        .then((r) => (this.groundY = r.heights[0] / 100))
        .catch(ignoreCancel);
      this.treesNear = this.countTrees(cam, 40);
    }
    let cars = 0;
    for (const c of this.traffic.sim.cars) if ((c.cur[0] - cam.x) ** 2 + (c.cur[2] - cam.z) ** 2 < 80 * 80) cars++;
    const walking = this.nav.mode !== 'fly' && this.nav.player !== null;
    const env: ListenerEnv = {
      height: walking ? 1.6 : cam.y - this.groundY,
      indoors: this.solid.indoors(gx, gy, gz),
      trees: this.treesNear,
      cars,
      speed: this.camSpeed,
    };
    this.sound.update(dt, this.camera, env, this.traffic.sim.cars, this.traffic.alpha, this.nav.audioState());
  }

  /** Trees within `r` m of `p` as 0 … 1 (a dozen or more reads as a park). */
  private countTrees(p: THREE.Vector3, r: number): number {
    let n = 0;
    const m = new THREE.Matrix4();
    const w = new THREE.Vector3();
    for (const t of this.tiles.tiles.values()) {
      if (!t.city) continue;
      for (const c of t.city.children) {
        if (!(c instanceof THREE.InstancedMesh) || !c.name.startsWith('tree') || c.name.includes(':')) continue;
        if (!c.boundingSphere) c.computeBoundingSphere();
        const bs = c.boundingSphere!.clone().applyMatrix4(c.matrixWorld);
        if (bs.center.distanceTo(p) > bs.radius + r) continue;
        for (let i = 0; i < c.count; i++) {
          c.getMatrixAt(i, m);
          w.setFromMatrixPosition(m).applyMatrix4(c.matrixWorld);
          if ((w.x - p.x) ** 2 + (w.z - p.z) ** 2 < r * r) n++;
        }
      }
    }
    return Math.min(1, n / 12);
  }

  private selectedText(): string {
    const s = this.interiors.selected;
    if (!s) return '';
    const f = this.interiors.floors.get(s.floorKey);
    const r = f?.result.rooms[s.room];
    if (!f || !r) return '';
    const placed = r.objects.length;
    const rej = r.debug?.rejected.length ?? 0;
    const regions = r.debug?.walkable.length ?? 0;
    return `<br/>room ${s.room} (${r.type}) floor ${f.floor}: ${placed} objects, ${rej} rejected, walkable regions ${regions}`;
  }

  /**
   * Quality presets: low = no shadows, AO or bloom, FXAA, pixel ratio 1; mid = 3 cascades of 2048² to
   * 900 m, AO at half resolution, 4× MSAA, bloom, pixel ratio ≤ 1.5; high = 4 cascades of 4096² to
   * 1400 m, full-resolution AO, pixel ratio ≤ 2. The shadow/AO switches override the preset. Kept in the URL.
   */
  applyGraphics(g: GraphicsSettings): void {
    this.graphics = g;
    const hi = g.quality === 'high';
    this.basePixelRatio = Math.min(window.devicePixelRatio, g.quality === 'low' ? 1 : hi ? 2 : 1.5);
    this.renderer.setPixelRatio(this.basePixelRatio * this.resScale);
    if (g.shadows) this.shadows.enable(hi ? { cascades: 4, maxFar: 1400, mapSize: 4096 } : { cascades: 3, maxFar: 900, mapSize: 2048 });
    else this.shadows.disable();
    this.postfx.aoScale = hi ? 1 : 0.5;
    this.msaaWanted = g.quality === 'low' ? 0 : 4;
    this.postfx.configure({ msaa: this.msaaWanted, bloom: g.quality !== 'low' });
    if (this.surfaces) this.surfaces.normals = g.quality !== 'low';
    if (this.traffic) this.traffic.counts = CROWD[g.quality];
    if (this.sound) this.sound.voiceCount = VOICES[g.quality];
    this.hud?.setGraphics(g);
    this.postfx.ao = g.ao;
    this.onResize();
    try {
      localStorage.setItem(GRAPHICS_STORE, JSON.stringify(g));
    } catch {
      /* not saved */
    }
    const url = new URL(location.href);
    url.searchParams.set('quality', g.quality);
    url.searchParams.set('shadows', g.shadows ? '1' : '0');
    url.searchParams.set('ao', g.ao ? '1' : '0');
    history.replaceState(null, '', url);
  }

  private onResize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.shadows.resize();
    this.postfx.setSize(w, h);
  }
}

/** Controls shown for a moment when a mode starts. */
const HINTS: Record<'fly' | 'walk' | 'drive', string> = {
  fly: '<b>Fly</b> · WASD move · E/Space up · Q/C down · Shift faster · wheel sets speed',
  walk: '<b>Walk</b> · WASD walk · Shift run · Space jump · F get into your car',
  drive: '<b>Drive</b> · W/S throttle, brake, reverse · A/D steer · Space handbrake · F get out · R reset · mouse looks around',
};

const GRAPHICS_STORE = 'perpcity.graphics';
